import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IWorkOrderPo } from "mobx-stores/_models/work-order-po/work-order-po";
import { useStores } from "mobx-stores/_root-store";
import { CustomerDealingAndActiveInfo } from "pages/common/customer-dealing-and-active-info";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NumberText } from "share-components/atomics/number-text";
import {
  DetailIconButton,
  ExpandButton,
} from "share-components/mui-wrap/button/action-button";
import { XGroupCell } from "share-components/mui-wrap/table/x-group-cell";
import { XSubRow } from "share-components/mui-wrap/table/x-sub-row";
import { XTablePagination } from "share-components/mui-wrap/table/x-table-pagination";
import { WorkOrderPoStatus } from "../components/status";
import { roundTo } from "commons/ultilities";

export const WorkOrderPoTable = observer(() => {
  const {
    workOrderPoManagement: {
      workOrdersPo,
      getWorkOrdersPo,
      pagination,
      searchConditionAsNaturalLanguage,
    },
  } = useStores();

  useEffect(() => {
    getWorkOrdersPo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPageChanged = useCallback(
    (_: any, page: number) => {
      pagination.setProp("pageIndex", page - 1);
      getWorkOrdersPo();
    },
    [getWorkOrdersPo, pagination],
  );

  return (
    <XTablePagination
      paginationProps={{
        count: pagination.totalPages,
        page: pagination.pageIndex + 1,
        onChange: onPageChanged,
      }}
      searchConditionAsNaturalLanguage={searchConditionAsNaturalLanguage}
    >
      <TableHead>
        <TableRow>
          <TableCell rowSpan={2}></TableCell>
          <TableCell rowSpan={2}>Đơn Hàng</TableCell>
          <TableCell rowSpan={2}>STT</TableCell>
          <TableCell colSpan={4} align="center">
            Thông Tin Đặt Hàng
          </TableCell>
          <TableCell colSpan={6} align="center">
            Theo Dõi Lệnh Sản Xuất
          </TableCell>
          <TableCell rowSpan={2}></TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ top: "50px" }}>Tên Giao Dịch</TableCell>
          <TableCell sx={{ top: "50px" }}>Tên Hàng</TableCell>
          <TableCell sx={{ top: "50px" }}>Ngày Giao Hàng</TableCell>
          <TableCell sx={{ top: "50px" }}>Số Lượng Giao</TableCell>

          <TableCell sx={{ top: "50px" }}>Ngày Sản Xuất</TableCell>
          <TableCell sx={{ top: "50px" }}>Số Lệnh Sản Xuất</TableCell>
          <TableCell sx={{ top: "50px" }}>Số Lượng Sản Xuất</TableCell>
          <TableCell sx={{ top: "50px" }}>S/L Thành Phẩm</TableCell>
          <TableCell sx={{ top: "50px" }}>Tỉ Lệ Hao Hụt</TableCell>
          <TableCell sx={{ top: "50px" }}>Trạng Thái</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {workOrdersPo?.map((workOrderPo, index) => (
          <Row key={workOrderPo.id || index} workOrderPo={workOrderPo}></Row>
        ))}
      </TableBody>
    </XTablePagination>
  );
});

type RowProps = {
  workOrderPo: IWorkOrderPo;
};

const Row = observer(({ workOrderPo }: RowProps) => {
  const {
    workOrderPoManagement: { setProp },
  } = useStores();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const goDetailHandler = useCallback(
    (order: IWorkOrderPo) => {
      navigate(`${order.id}`);
      setProp("workOrderPoDetail", cloneDeep(order));
    },
    [navigate, setProp],
  );
  return (
    <Fragment>
      <TableRow hover>
        <TableCell sx={{ width: "30px" }}>
          <ExpandButton
            onClick={() => setOpen(!open)}
            open={open}
          ></ExpandButton>
        </TableCell>
        <TableCell>{workOrderPo.purchaseOrder?.poNumber || ""}</TableCell>
        <TableCell>{workOrderPo.orderNumber || ""}</TableCell>
        <TableCell>
          {workOrderPo?.customerDisplay?.dealingName ? (
            <CustomerDealingAndActiveInfo
              customer={workOrderPo.customerDisplay}
            ></CustomerDealingAndActiveInfo>
          ) : (
            ""
          )}
        </TableCell>
        <TableCell>
          {workOrderPo.purchaseOrderItem?.product?.name || ""}
        </TableCell>
        <TableCell>
          {dateTimeDisplay(workOrderPo.purchaseOrderItem?.deliveryDate || "")}
        </TableCell>
        <TableCell>
          <NumberText
            value={workOrderPo.purchaseOrderItem?.quantityStandard || 0}
          ></NumberText>
        </TableCell>
        <TableCell>{dateTimeDisplay(workOrderPo.date || "")}</TableCell>
        <TableCell>{workOrderPo.woNumber || ""}</TableCell>
        <TableCell>
          <NumberText
            value={workOrderPo.poItemProductionAmount || 0}
          ></NumberText>
        </TableCell>
        <TableCell>
          <NumberText value={workOrderPo.completedAmount || 0}></NumberText>
        </TableCell>
        <TableCell>
          <NumberText value={roundTo(workOrderPo.lossRate || 0, 2)}></NumberText>
        </TableCell>
        <TableCell>
          {workOrderPo.status && (
            <WorkOrderPoStatus
              status={workOrderPo.status}
              isCompensating={workOrderPo?.isCompensating ?? false}
            ></WorkOrderPoStatus>
          )}
        </TableCell>
        <TableCell width={"20px"}>
          <DetailIconButton
            onClick={() => goDetailHandler(workOrderPo)}
          ></DetailIconButton>
        </TableCell>
      </TableRow>
      {open && (
        <XSubRow>
          <XGroupCell colSpan={14} headerTitle="Theo Dõi Đơn Đặt Hàng:">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ backgroundColor: "#FDE9D9 !important" }}
                  >
                    Thông Tin Khách Hàng
                  </TableCell>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ backgroundColor: "#DAEEF3 !important" }}
                  >
                    Thông Tin Đặt Hàng
                  </TableCell>
                  <TableCell rowSpan={2}>Ghi chú</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ top: "50px" }}>Tên Giao Dịch</TableCell>
                  <TableCell>Tên Hàng</TableCell>
                  <TableCell>Mã Hàng</TableCell>
                  <TableCell>Mã Vạch</TableCell>

                  <TableCell>Ngày Đặt</TableCell>
                  <TableCell>Ngày Giao</TableCell>
                  <TableCell>S/L Trên</TableCell>
                  <TableCell>S/L Giao</TableCell>
                  <TableCell>S/L Dưới</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    {workOrderPo.customerDisplay?.dealingName ? (
                      <CustomerDealingAndActiveInfo
                        customer={workOrderPo.customerDisplay}
                      ></CustomerDealingAndActiveInfo>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {workOrderPo.purchaseOrderItem?.product?.name || ""}
                  </TableCell>
                  <TableCell>
                    {workOrderPo.purchaseOrderItem?.product?.code || ""}
                  </TableCell>
                  <TableCell>
                    {workOrderPo.purchaseOrderItem?.product?.barcode || ""}
                  </TableCell>
                  <TableCell>
                    {dateTimeDisplay(workOrderPo.purchaseOrder?.poDate)}
                  </TableCell>
                  <TableCell>
                    {dateTimeDisplay(
                      workOrderPo.purchaseOrderItem?.deliveryDate || "",
                    )}
                  </TableCell>
                  <TableCell>
                    <NumberText
                      value={workOrderPo.purchaseOrderItem?.quantityUpper || 0}
                    ></NumberText>
                  </TableCell>
                  <TableCell>
                    <NumberText
                      value={
                        workOrderPo.purchaseOrderItem?.quantityStandard || 0
                      }
                    ></NumberText>
                  </TableCell>
                  <TableCell>
                    <NumberText
                      value={workOrderPo.purchaseOrderItem?.quantityLower || 0}
                    ></NumberText>
                  </TableCell>
                  <TableCell>{workOrderPo.note || ""}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </XGroupCell>
        </XSubRow>
      )}
    </Fragment>
  );
});
